import arrayContains from 'utils/arrayUtils';
import {
    forbiddenNationalities,
    forbiddenNationalitiesForMortgage,
    GENDER,
    PRODUCTS,
    switzLandCountryId,
    UPPER_AGE_LIMIT,
} from 'constants/constants';

import { TMainClientData } from '../../datasource/CreatePortfolio/CreatePortfolioManager.types';

/**
 * Validates US tax status.
 * Converts values to strings to safely handle mixed types from legacy APIs.
 */
const isUsPerson = (taxData: any) => {
    const fields = [
        taxData?.hasUsResidence,
        taxData?.classedUsPerson,
        taxData?.isUsCitizenOrSubjectToUsTax,
        taxData?.isUsPerson,
    ];

    // Returns true if any field is explicitly "true" or true (boolean)
    return fields.some((val) => String(val) === 'true');
};

const getUpperAgeLimit = (gender: number) => (gender === GENDER.male ? UPPER_AGE_LIMIT.male : UPPER_AGE_LIMIT.female);

export const getRestrictedClientMessage = ({
    data,
    selectedProductType,
    isMortgage = false,
}: {
    data?: TMainClientData;
    selectedProductType?: string;
    isMortgage?: boolean;
}) => {
    if (!data) return undefined;

    // Mortgage Specific Validation
    if (isMortgage) {
        if (forbiddenNationalitiesForMortgage?.includes(data.nationality)) {
            return 'portfolio.createNewPortfolio.nationalityNotAllowed';
        }

        return undefined;
    }

    // Location Validation
    if (data.countryOfResidence !== switzLandCountryId) {
        return 'portfolio.createNewPortfolio.produntOnlyForSwitzland';
    }

    // Nationality Validation
    if (forbiddenNationalities.includes(data.nationality)) {
        if (data?.nationality === 220) {
            return 'clientDashboard.unableToOpenNewPortfolioBody';
        }

        return 'portfolio.createNewPortfolio.nationalityNotAllowed';
    }

    // Age & Product Type Validation
    const age = data.age ?? 0;
    const isOutsideAgeRange = age < 17 || age >= getUpperAgeLimit(data.gender);

    if (
        isOutsideAgeRange
        && selectedProductType
        && selectedProductType !== PRODUCTS.bbb
    ) {
        const allowsEmploymentContinuation = arrayContains(
            [PRODUCTS.aaa, PRODUCTS.aaap],
            selectedProductType,
        );

        // If they are outside age range, they can only proceed if the product
        // allows employment continuation and they have that flag set.
        const isRestricted = data.employmentContinuation
            ? !allowsEmploymentContinuation
            : true;

        if (isRestricted) {
            return 'portfolio.createNewPortfolio.productIsNotAccaptableForThisAge';
        }
    }

    // Tax Compliance Validation
    if (isUsPerson(data.taxData)) {
        return 'clientDashboard.unableToOpenNewPortfolioBody';
    }

    return undefined;
};
